import { Button, Skeleton } from "@tellescope/ui";
import { Badge, Dialog } from "@tellescope/design-system";
import { formatDateTime } from "@tellescope/utils";
import { useTranslation } from "react-i18next";
import { ArrowLeft, CheckCircle2, Clock, Loader2 } from "lucide-react";
import type { FormResponse } from "@tellescope/types-models";
import { renderAnswer } from "../../../shared/forms";
import { useOrganization } from "../../../shared/store/organizationStore";
import { useIframeToken } from "../../hooks/useIframeToken";
import { buildFormCompletionURL } from "../../../config/portal";
import { getEnduserVisibleResponses } from "../../utils/getEnduserVisibleResponses";

interface FormResponseDialogProps {
  formResponse: FormResponse | null;
  titleOverride?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FormAnswerProps {
  title: string;
  answer: NonNullable<FormResponse["responses"]>[number]["answer"];
  answerIsHTML?: boolean;
}

function FormAnswer({ title, answer, answerIsHTML }: FormAnswerProps) {
  const renderedAnswer = renderAnswer(answer, answerIsHTML);
  if (!renderedAnswer) return null;

  return (
    <div className="border-b border-border pb-3 last:border-b-0">
      <div className="text-sm font-medium text-muted-foreground mb-1">{title}</div>
      <div className="text-sm text-foreground">{renderedAnswer}</div>
    </div>
  );
}

function FormResponseSkeleton() {
  return (
    <div className="space-y-4 p-4">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-5 w-2/3" />
        </div>
      ))}
    </div>
  );
}

function EmptyResponses() {
  const { t } = useTranslation("forms");

  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="text-center text-muted-foreground">
        <p className="text-sm">{t("No responses recorded")}</p>
      </div>
    </div>
  );
}

export function FormResponseDialog({ formResponse, titleOverride, open, onOpenChange }: FormResponseDialogProps) {
  const { t } = useTranslation("forms");
  const organization = useOrganization();

  const isSubmitted = !!formResponse?.submittedAt;
  const hasDraft = !!formResponse?.draftSavedAt;
  const isPending = !isSubmitted;

  // Fetch a fresh token for the iframe when dialog opens for a pending form
  const { token: iframeToken, isLoading: isLoadingToken } = useIframeToken({
    enabled: open && isPending && !!formResponse,
  });

  const visibleResponses = getEnduserVisibleResponses(formResponse?.responses);

  // Build the iframe URL for pending forms (using the fresh iframe token)
  // Always uses subdomain-based URL for consistent embedding (not customPortalURL)
  const formCompletionURL =
    isPending && formResponse && organization && iframeToken && formResponse.accessCode
      ? buildFormCompletionURL({
          organization: { subdomain: organization.subdomain },
          formId: formResponse.formId,
          accessCode: formResponse.accessCode,
          authToken: iframeToken,
          automationStepId: formResponse.automationStepId,
          hideBg: true,
          startAtNext: true,
        })
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className="max-w-lg h-[85vh] flex flex-col p-0 gap-0" closeOnInteractOutside={!isPending}>
        {/* Header */}
        <Dialog.Header className="px-4 py-3 border-b flex-shrink-0">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 md:hidden" onClick={() => onOpenChange(false)}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <Dialog.Title className="text-base font-medium truncate pr-8">
              {titleOverride ?? formResponse?.formTitle}
            </Dialog.Title>
          </div>
        </Dialog.Header>

        {/* Metadata section - only for submitted forms */}
        {formResponse && isSubmitted && (
          <div className="px-4 py-3 border-b bg-muted/30 flex-shrink-0">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span>{t("Submitted {{timestamp}}", { timestamp: formatDateTime(formResponse.submittedAt!) })}</span>
            </div>

            {/* Show scores if available */}
            {formResponse.scores && formResponse.scores.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formResponse.scores.map((score, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs">
                    {score.title}: {score.value}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Content area */}
        <div className="flex-1 overflow-hidden">
          {!formResponse ? (
            <FormResponseSkeleton />
          ) : isPending && isLoadingToken ? (
            // Loading token for iframe
            <div className="flex-1 flex items-center justify-center p-8 h-full">
              <div className="text-center text-muted-foreground">
                <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin" />
                <p className="text-sm">{t("Loading form...")}</p>
              </div>
            </div>
          ) : isPending && formCompletionURL ? (
            // Show iframe for pending forms
            <iframe
              src={formCompletionURL}
              className="w-full h-full border-0"
              title={titleOverride ?? formResponse.formTitle}
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            />
          ) : isPending && !formCompletionURL ? (
            // Missing data to build URL (token fetch failed or missing accessCode)
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center text-muted-foreground">
                <Clock className="h-8 w-8 mx-auto mb-2 text-amber-600" />
                <p className="text-sm font-medium mb-1">{hasDraft ? t("Draft saved") : t("Awaiting completion")}</p>
                <p className="text-xs">
                  {hasDraft && formResponse.draftSavedAt
                    ? formatDateTime(formResponse.draftSavedAt)
                    : t("This form is ready to be completed")}
                </p>
              </div>
            </div>
          ) : visibleResponses.length === 0 ? (
            <EmptyResponses />
          ) : (
            <div className="p-4 space-y-4 overflow-y-auto h-full">
              {visibleResponses.map((response, idx) => (
                <FormAnswer
                  key={response.fieldId || response.externalId || idx}
                  title={response.fieldTitle}
                  answer={response.answer}
                  answerIsHTML={response.answerIsHTML}
                />
              ))}
            </div>
          )}
        </div>
      </Dialog.Content>
    </Dialog>
  );
}
